/** Strings of the AI suggestion pages (`/suggestions/learnings`, `/suggestions/work-items`, review run): `sugEn` is the source, `sugCs` must have the same keys. */
export const sugEn = {
  sug: {
    learnings: {
      tabs: {
        draft: "Draft",
        active: "Active",
        expired: "Expired",
        dismissed: "Dismissed",
        all: "All",
      },
      loadError: "Couldn't load learnings.",
      saveError: "Couldn't save that decision.",
      convertError: "Couldn't send that to Metoděj.",
      mineStarted: "Mining started — check back shortly, or switch tabs to refresh.",
      mineNoAi: "AI isn't configured.",
      mineError: "Couldn't start mining.",
      title: "Hubert's Learnings",
      intro:
        "Patterns mined from confirmed corrections to Hubert's own past proposals — rejections with a reason, and approvals edited before applying. A pattern only reaches Hubert's prompt once you Activate it, and Deactivating is an immediate, total revert.",
      mineNow: "Mine now",
      starting: "Starting…",
      emptyDraft: 'Nothing waiting — run "Mine now" or wait for the weekly pass.',
      empty: "Nothing here.",
      global: "Global",
      evidence_one: "{{count}} evidence item",
      evidence_few: "{{count}} evidence items",
      evidence_many: "{{count}} evidence items",
      evidence_other: "{{count}} evidence items",
      mined: "· mined {{date}}",
      activatedBy: "· activated by {{name}}",
      dismissedBy: "· dismissed by {{name}}",
      activate: "Activate",
      dismiss: "Dismiss",
      sentTo: "Sent to Metoděj — ",
      openTicket: "open ticket",
      targetProject: "Target project for the code fix",
      pickProject: "— pick Hub's own project —",
      sendToMetodej: "Send to Metoděj",
    },
    workItems: {
      scanStarted:
        "Duplicate scan started — it runs in the background. New proposals appear under Pending; progress and any failure show on the Sync page.",
      scanFailedReason: "Couldn't start the scan: {{reason}}",
      scanFailed: "Couldn't start the scan — try again.",
      title: "Work item suggestions",
      intro:
        "Everything the AI proposes about tickets — from meeting transcripts, WhatsApp chats, forwarded client emails, and the duplicate scan. Approve the ones worth acting on (it applies them through the normal flow) or reject the rest. Each shows the source it's based on. Proposed time entries live on <link>Time suggestions</link>.",
      reviewOneByOne: "Review one by one",
      scanning: "Scanning…",
      scanDuplicates: "Scan for duplicates",
    },
    review: {
      loadError: "Couldn't load suggestions.",
      rejectError: "Couldn't reject this suggestion.",
      title: "Review suggestions",
      intro:
        "One suggestion at a time — edit it if it needs it, then approve or reject; the next one loads by itself. The whole pile is on <link>Work item suggestions</link>.",
      progress: "Suggestion {{current}} of {{total}}",
      reviewed: "{{total}} reviewed",
      decided: "{{count}} decided",
      previousRejected: "Previous suggestion rejected.",
      previousApplied: "Previous suggestion applied — {{message}}.",
      done: "done",
      emptyText:
        "Nothing waiting — new suggestions from emails, WhatsApp, Discord, meetings, and the duplicate scan land here.",
      checkAgain: "Check again",
      caughtUp_one:
        "All caught up — you went through {{count}} suggestion and decided {{decided}}.",
      caughtUp_few:
        "All caught up — you went through {{count}} suggestions and decided {{decided}}.",
      caughtUp_many:
        "All caught up — you went through {{count}} suggestions and decided {{decided}}.",
      caughtUp_other:
        "All caught up — you went through {{count}} suggestions and decided {{decided}}.",
      moreWaiting: " {{count}} more are still waiting — load the next batch to carry on.",
      loadNext: "Load the next batch",
      checkNew: "Check for new",
      backToQueue: "Back to the queue",
      back: "Back",
      next: "Next",
      skip: "Skip for now",
    },
  },
};

export const sugCs: typeof sugEn = {
  sug: {
    learnings: {
      tabs: {
        draft: "Návrhy",
        active: "Aktivní",
        expired: "Vypršelé",
        dismissed: "Zamítnuté",
        all: "Vše",
      },
      loadError: "Poznatky se nepodařilo načíst.",
      saveError: "Rozhodnutí se nepodařilo uložit.",
      convertError: "Poznatek se nepodařilo poslat Metoději.",
      mineStarted: "Těžení začalo — za chvíli se vraťte, nebo přepnutím záložky obnovte seznam.",
      mineNoAi: "AI není nakonfigurovaná.",
      mineError: "Těžení se nepodařilo spustit.",
      title: "Poznatky Huberta",
      intro:
        "Vzorce vytěžené z potvrzených oprav dřívějších návrhů Huberta — zamítnutí s důvodem a schválení upravená před použitím. Vzorec se dostane do promptu Huberta, až když ho aktivujete, a deaktivace je okamžitý a úplný návrat zpět.",
      mineNow: "Vytěžit teď",
      starting: "Spouštím…",
      emptyDraft: "Nic nečeká — spusťte „Vytěžit teď“ nebo počkejte na týdenní průchod.",
      empty: "Nic tu není.",
      global: "Globální",
      evidence_one: "{{count}} doklad",
      evidence_few: "{{count}} doklady",
      evidence_many: "{{count}} dokladu",
      evidence_other: "{{count}} dokladů",
      mined: "· vytěženo {{date}}",
      activatedBy: "· aktivoval(a) {{name}}",
      dismissedBy: "· zamítl(a) {{name}}",
      activate: "Aktivovat",
      dismiss: "Zamítnout",
      sentTo: "Odesláno Metoději — ",
      openTicket: "otevřít tiket",
      targetProject: "Cílový projekt pro opravu v kódu",
      pickProject: "— vyberte vlastní projekt Hubu —",
      sendToMetodej: "Poslat Metoději",
    },
    workItems: {
      scanStarted:
        "Hledání duplicit začalo — běží na pozadí. Nové návrhy se objeví v Čekajících; průběh i případná chyba jsou na stránce Synchronizace.",
      scanFailedReason: "Hledání duplicit se nepodařilo spustit: {{reason}}",
      scanFailed: "Hledání duplicit se nepodařilo spustit — zkuste to znovu.",
      title: "Návrhy tiketů",
      intro:
        "Vše, co AI navrhuje k tiketům — z přepisů schůzek, chatů na WhatsAppu, přeposlaných e-mailů od klientů a z hledání duplicit. Schvalte ty, které stojí za realizaci (provedou se běžným postupem), ostatní zamítněte. U každého je vidět zdroj, ze kterého vychází. Navržené časové záznamy najdete v <link>Návrzích času</link>.",
      reviewOneByOne: "Projít jeden po druhém",
      scanning: "Hledám…",
      scanDuplicates: "Najít duplicity",
    },
    review: {
      loadError: "Návrhy se nepodařilo načíst.",
      rejectError: "Návrh se nepodařilo zamítnout.",
      title: "Procházení návrhů",
      intro:
        "Jeden návrh po druhém — v případě potřeby ho upravte, pak schvalte nebo zamítněte; další se načte sám. Celá hromada je v <link>Návrzích tiketů</link>.",
      progress: "Návrh {{current}} z {{total}}",
      reviewed: "Projito: {{total}}",
      decided: "Rozhodnuto: {{count}}",
      previousRejected: "Předchozí návrh byl zamítnut.",
      previousApplied: "Předchozí návrh byl proveden — {{message}}.",
      done: "hotovo",
      emptyText:
        "Nic nečeká — nové návrhy z e-mailů, WhatsAppu, Discordu, schůzek a z hledání duplicit přistanou tady.",
      checkAgain: "Zkontrolovat znovu",
      caughtUp_one: "Máte hotovo — prošli jste {{count}} návrh a rozhodli {{decided}}.",
      caughtUp_few: "Máte hotovo — prošli jste {{count}} návrhy a rozhodli {{decided}}.",
      caughtUp_many: "Máte hotovo — prošli jste {{count}} návrhu a rozhodli {{decided}}.",
      caughtUp_other: "Máte hotovo — prošli jste {{count}} návrhů a rozhodli {{decided}}.",
      moreWaiting: " Dalších {{count}} stále čeká — načtěte další dávku a pokračujte.",
      loadNext: "Načíst další dávku",
      checkNew: "Zkontrolovat nové",
      backToQueue: "Zpět do fronty",
      back: "Zpět",
      next: "Další",
      skip: "Zatím přeskočit",
    },
  },
};
